/**
 * Frequently Bought Together + bundle discount sync (theme-only).
 * Discount applies only when the full bundle is selected / in cart.
 * Removing any bundle item clears the discount code.
 */
(function () {
  const STORAGE_KEY = 'gg_fbt_discount';

  function addThousandSeparators(value) {
    const str = String(value);
    const parts = str.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  function formatMoney(cents, moneyFormat) {
    const value = Number(cents) / 100;
    const amount = value.toFixed(2);
    const amountNoDecimals = String(Math.round(value));
    const amountWithCommas = addThousandSeparators(amount);
    const amountNoDecimalsWithCommas = addThousandSeparators(amountNoDecimals);

    if (!moneyFormat) return `Rs.${amountNoDecimalsWithCommas}`;

    return moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithCommas)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimalsWithCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimalsWithCommas)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amountWithCommas.replace(/,/g, "'"))
      .replace(/\{\{\s*amount\s*\}\}/g, amountWithCommas);
  }

  function bundleId() {
    const hex = Math.random().toString(16).slice(2, 12).toUpperCase();
    return `FBT-BUNDLE-P-${hex}`;
  }

  function getStoredDiscount() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch (e) {
      return null;
    }
  }

  function setStoredDiscount(data) {
    if (!data) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  async function fetchCart() {
    const res = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    return res.json();
  }

  async function updateDiscountCode(code) {
    const res = await fetch(`${window.routes?.cart_url || '/cart'}/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ discount: code || '' }),
    });
    if (!res.ok) throw new Error('Discount update failed');
    return res.json();
  }

  /**
   * Before adding an FBT bundle, remove plain (non-FBT) cart lines for the
   * same products so the simple ATC copy does not stay as a duplicate.
   */
  async function removeNonFbtDuplicates(products) {
    const productIds = new Set(
      products.map((p) => Number(p.id)).filter((id) => Number.isFinite(id) && id > 0)
    );
    const variantIds = new Set(
      products.map((p) => Number(p.variant_id)).filter((id) => Number.isFinite(id) && id > 0)
    );
    if (!productIds.size && !variantIds.size) return null;

    const cart = await fetchCart();
    const updates = {};

    for (const item of cart.items || []) {
      if (isFbtLine(item)) continue;
      const sameProduct = productIds.has(Number(item.product_id));
      const sameVariant = variantIds.has(Number(item.variant_id));
      if (!sameProduct && !sameVariant) continue;
      if (item.key) updates[item.key] = 0;
    }

    if (!Object.keys(updates).length) return cart;

    const res = await fetch(`${window.routes?.cart_url || '/cart'}/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) throw new Error('Could not clear duplicate cart items');
    return res.json();
  }

  const MIN_FBT_ITEMS_FOR_DISCOUNT = 2;

  function isFbtLine(item) {
    if (!item || Number(item.quantity) <= 0) return false;
    const props = item.properties || {};
    return Boolean(props._fbt_bundle || props['FBT Bundle']);
  }

  function countFbtItems(cart) {
    return (cart.items || []).filter(isFbtLine).length;
  }

  // Discount only when at least 2 FBT-tagged lines remain in cart
  function hasCompleteBundle(cart) {
    return countFbtItems(cart) >= MIN_FBT_ITEMS_FOR_DISCOUNT;
  }

  function hasIncompleteBundle(cart) {
    const n = countFbtItems(cart);
    return n > 0 && n < MIN_FBT_ITEMS_FOR_DISCOUNT;
  }

  function resolveFbtDiscountCode(cart, stored) {
    if (stored?.code) return stored.code;
    const fromCodes = (cart.discount_codes || []).find((d) => /FBT/i.test(d.code || ''));
    if (fromCodes?.code) return fromCodes.code;
    const fromApps = (cart.cart_level_discount_applications || []).find((d) =>
      /FBT/i.test(d.title || '')
    );
    if (fromApps?.title) return fromApps.title;
    return 'FBTBUNDLE';
  }

  function cartHasFbtDiscount(cart, code) {
    const codeUp = (code || '').toUpperCase();
    const inCodes = (cart.discount_codes || []).some(
      (d) => (d.code || '').toUpperCase() === codeUp || /FBT/i.test(d.code || '')
    );
    const inApps = (cart.cart_level_discount_applications || []).some((d) =>
      /FBT/i.test(d.title || '')
    );
    return inCodes || inApps;
  }

  async function syncBundleDiscount({ forceApply = false, forceClear = false } = {}) {
    let cart = await fetchCart();
    const stored = getStoredDiscount();
    const code = resolveFbtDiscountCode(cart, stored);
    const fbtCount = countFbtItems(cart);
    const complete = fbtCount >= MIN_FBT_ITEMS_FOR_DISCOUNT;
    const incomplete = fbtCount > 0 && fbtCount < MIN_FBT_ITEMS_FOR_DISCOUNT;
    const hasCode = cartHasFbtDiscount(cart, code);
    const shouldClear = forceClear || incomplete || (fbtCount === 0 && hasCode) || (!complete && hasCode);

    try {
      if (shouldClear && (hasCode || forceClear || incomplete)) {
        cart = await updateDiscountCode('');
        // Confirm clear — some stores need a second pass
        if (cartHasFbtDiscount(cart, code)) {
          cart = await updateDiscountCode('');
        }
      } else if ((complete || forceApply) && code && fbtCount >= MIN_FBT_ITEMS_FOR_DISCOUNT) {
        cart = await updateDiscountCode(code);
        if (!stored?.code) {
          setStoredDiscount({ code, percent: stored?.percent || 10 });
        }
      }
    } catch (err) {
      console.warn('[FBT] discount sync', err);
    }

    return cart;
  }

  async function refreshCartDrawer(cart) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') return;

    if (cart?.item_count > 0) {
      drawer.classList.remove('is-empty');
      drawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
    }

    // Re-fetch sections for drawer HTML
    const sectionRes = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
    const sections = await sectionRes.json();
    drawer.renderContents({ ...cart, sections });
  }

  // Expose for cart remove hooks
  window.GlossyFBT = {
    syncBundleDiscount,
    refreshCartDrawer,
    getStoredDiscount,
    setStoredDiscount,
  };

  if (!customElements.get('frequently-bought-together')) {
    customElements.define(
      'frequently-bought-together',
      class FrequentlyBoughtTogether extends HTMLElement {
        async connectedCallback() {
          try {
            this.products = JSON.parse(this.querySelector('[data-fbt-products]')?.textContent || '[]');
          } catch (e) {
            this.products = [];
          }

          this.discountPercent = Number(this.dataset.discountPercent || 0);
          this.discountCode = (this.dataset.discountCode || '').trim();
          this.moneyFormat = this.dataset.moneyFormat || '';
          this.maxProducts = Number(this.dataset.maxProducts || 2);

          this.thumbsEl = this.querySelector('[data-fbt-thumbs]');
          this.listEl = this.querySelector('[data-fbt-list]');
          this.compareEl = this.querySelector('[data-fbt-compare]');
          this.priceEl = this.querySelector('[data-fbt-price]');
          this.submitBtn = this.querySelector('[data-fbt-submit]');
          this.spinner = this.querySelector('.fbt__spinner');

          // Fallback: related product recommendations when metafield/list empty
          if (this.products.filter((p) => !p.is_current).length === 0) {
            await this.loadRecommendations();
          }

          // Hide widget if still only one product
          if (this.products.length < 2) {
            this.hidden = true;
            return;
          }

          this.selected = new Set(this.products.filter((p) => p.available).map((p) => p.variant_id));
          this.render();
          this.submitBtn?.addEventListener('click', () => this.addSelected());

          // Keep current product variant in sync
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (!event?.data?.variant) return;
              const sectionId = this.dataset.sectionId;
              if (event.data.sectionId && String(event.data.sectionId) !== String(sectionId)) return;
              const variant = event.data.variant;
              const current = this.products.find((p) => p.is_current);
              if (!current) return;

              const wasSelected = this.selected.has(current.variant_id);
              this.selected.delete(current.variant_id);
              current.variant_id = variant.id;
              current.available = variant.available;
              current.price = variant.price;
              current.compare_at_price = variant.compare_at_price;
              current.price_formatted = formatMoney(variant.price, this.moneyFormat);
              if (variant.compare_at_price) {
                current.compare_at_price_formatted = formatMoney(variant.compare_at_price, this.moneyFormat);
              } else {
                current.compare_at_price_formatted = null;
              }
              if (variant.featured_image?.src) {
                current.image = variant.featured_image.src;
              }
              if (wasSelected && variant.available) this.selected.add(current.variant_id);
              this.render();
            });
          }
        }

        async loadRecommendations() {
          const productId = this.dataset.productId;
          if (!productId) return;
          try {
            const url = `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=${this.maxProducts}&intent=related`;
            const res = await fetch(url);
            if (!res.ok) return;
            const data = await res.json();
            // Prefer first in-stock variant — not variants[0] which may be a sold-out shade
            const extras = [];
            for (const p of data.products || []) {
              if (extras.length >= this.maxProducts) break;
              const variants = p.variants || [];
              const v = variants.find((variant) => variant.available) || null;
              if (!v) continue; // skip fully sold-out products
              const image = v.featured_image?.src || p.featured_image || p.images?.[0] || null;
              extras.push({
                id: p.id,
                handle: p.handle,
                title: p.title,
                is_current: false,
                variant_id: v.id,
                available: true,
                price: v.price,
                price_formatted: formatMoney(v.price, this.moneyFormat),
                compare_at_price: v.compare_at_price,
                compare_at_price_formatted: v.compare_at_price
                  ? formatMoney(v.compare_at_price, this.moneyFormat)
                  : null,
                image: image,
                url: p.url,
                variant_title: v.title,
              });
            }
            this.products = [...this.products, ...extras];
          } catch (err) {
            console.warn('[FBT] recommendations', err);
          }
        }

        disconnectedCallback() {
          this.unsub?.();
        }

        selectedProducts() {
          return this.products.filter((p) => this.selected.has(p.variant_id) && p.available);
        }

        // Bundle discount when at least 2 items are checked (not required to check all)
        hasBundleDiscount() {
          return this.selectedProducts().length >= 2 && this.discountPercent > 0;
        }

        totals() {
          const items = this.selectedProducts();
          const original = items.reduce((sum, p) => sum + Number(p.price || 0), 0);
          const applyDiscount = this.hasBundleDiscount();
          const discounted = applyDiscount
            ? Math.round(original * (1 - this.discountPercent / 100))
            : original;
          return { original, discounted, applyDiscount, count: items.length };
        }

        render() {
          this.renderThumbs();
          this.renderList();
          this.renderTotal();
          const count = this.selectedProducts().length;
          if (this.submitBtn) this.submitBtn.disabled = count === 0;
        }

        renderThumbs() {
          if (!this.thumbsEl) return;
          const selected = this.selectedProducts();
          // Show all products in thumb row, dim unchecked
          this.thumbsEl.innerHTML = this.products
            .map((p, index) => {
              const on = this.selected.has(p.variant_id);
              const plus =
                index < this.products.length - 1 ? `<span class="fbt__plus" aria-hidden="true">+</span>` : '';
              return `
                <div class="fbt__thumb${on ? '' : ' is-unchecked'}" title="${this.escape(p.title)}">
                  ${p.image ? `<img src="${p.image}" alt="${this.escape(p.title)}" width="72" height="72" loading="lazy">` : ''}
                </div>
                ${plus}
              `;
            })
            .join('');

          // If nothing selected, still ok
          void selected;
        }

        renderList() {
          if (!this.listEl) return;
          // Bundle discount UI: cut = current selling price, pink = after FBT %
          const applyBundleDiscount = this.hasBundleDiscount();

          this.listEl.innerHTML = this.products
            .map((p) => {
              const checked = this.selected.has(p.variant_id);
              const label = p.is_current ? `This item: ${p.title}` : p.title;
              const variantLabel =
                p.variant_title && p.variant_title !== 'Default Title' ? `${this.escape(p.variant_title)} ` : '';

              const currentPrice = Number(p.price || 0);
              const currentFormatted = p.price_formatted || formatMoney(currentPrice, this.moneyFormat);
              let priceHtml = `<span class="fbt__sale">${currentFormatted}</span>`;

              if (applyBundleDiscount && checked && currentPrice > 0) {
                const discounted = Math.round(currentPrice * (1 - this.discountPercent / 100));
                priceHtml = `<s class="fbt__cut">${currentFormatted}</s><span class="fbt__sale">${formatMoney(
                  discounted,
                  this.moneyFormat
                )}</span>`;
              }

              return `
                <li class="fbt__item">
                  <input
                    class="fbt__check"
                    type="checkbox"
                    data-variant-id="${p.variant_id}"
                    ${checked ? 'checked' : ''}
                    ${p.available ? '' : 'disabled'}
                    aria-label="${this.escape(label)}"
                  >
                  <div>
                    <a class="fbt__item-title" href="${p.url}">${this.escape(label)}</a>
                    <p class="fbt__item-meta">
                      ${variantLabel}${priceHtml}
                      ${p.available ? '' : ' — Sold out'}
                    </p>
                  </div>
                </li>
              `;
            })
            .join('');

          this.listEl.querySelectorAll('.fbt__check').forEach((input) => {
            input.addEventListener('change', () => {
              const id = Number(input.dataset.variantId);
              if (input.checked) this.selected.add(id);
              else this.selected.delete(id);
              this.render();
            });
          });
        }

        renderTotal() {
          const { original, discounted, applyDiscount } = this.totals();
          if (this.priceEl) this.priceEl.textContent = formatMoney(discounted, this.moneyFormat);
          if (this.compareEl) {
            if (applyDiscount && discounted < original) {
              this.compareEl.hidden = false;
              this.compareEl.textContent = formatMoney(original, this.moneyFormat);
            } else {
              this.compareEl.hidden = true;
              this.compareEl.textContent = '';
            }
          }
        }

        escape(str) {
          return String(str || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/"/g, '&quot;');
        }

        setLoading(loading) {
          this.submitBtn?.classList.toggle('loading', loading);
          this.submitBtn?.toggleAttribute('aria-disabled', loading);
          this.spinner?.classList.toggle('hidden', !loading);
        }

        async addSelected() {
          const items = this.selectedProducts();
          if (!items.length || this.submitBtn?.getAttribute('aria-disabled') === 'true') return;

          const withBundleDiscount = this.hasBundleDiscount();
          const id = withBundleDiscount ? bundleId() : null;
          const payloadItems = items.map((p) => {
            const line = { id: p.variant_id, quantity: 1 };
            if (withBundleDiscount && id) {
              line.properties = {
                _fbt_bundle: id,
                _fbt_size: String(items.length),
                _fbt_discount_percent: String(this.discountPercent || 0),
                'FBT Bundle': id,
              };
            }
            return line;
          });

          this.setLoading(true);

          try {
            // Drop plain ATC lines for the same products so bundle doesn't duplicate them
            await removeNonFbtDuplicates(items);

            const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            const sections = cart?.getSectionsToRender?.().map((s) => s.id) || ['cart-drawer', 'cart-icon-bubble'];

            const addRes = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({
                items: payloadItems,
                sections,
                sections_url: window.location.pathname,
              }),
            });

            const addJson = await addRes.json();
            if (addJson.status) {
              alert(addJson.description || addJson.message || 'Could not add items');
              return;
            }

            // Prevent empty-state grid layout on first open
            if (cart?.classList?.contains('is-empty')) {
              cart.classList.remove('is-empty');
            }
            cart?.querySelector?.('.drawer__inner')?.classList.remove('is-empty');

            if (withBundleDiscount && this.discountCode) {
              setStoredDiscount({
                code: this.discountCode,
                percent: this.discountPercent,
                bundleId: id,
                size: items.length,
              });
              await syncBundleDiscount({ forceApply: true });
            }

            // Refresh drawer with latest cart + sections (once)
            const latest = await fetchCart();
            if (cart?.renderContents) {
              if (addJson.sections) {
                cart.renderContents({ ...latest, sections: addJson.sections });
              } else {
                await refreshCartDrawer(latest);
              }
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fbt', cartData: latest });
            }
          } catch (err) {
            console.error('[FBT] add error', err);
            alert('Something went wrong adding the bundle. Please try again.');
          } finally {
            this.setLoading(false);
          }
        }
      }
    );
  }

  // Cart sync: when items change, drop discount unless 2+ FBT lines remain
  async function runCartDiscountSync(event) {
    if (event?.source === 'fbt' || event?.source === 'fbt-sync') return;
    if (window.__ggFbtSyncing) return;
    window.__ggFbtSyncing = true;
    try {
      const before = await fetchCart();
      const fbtBefore = countFbtItems(before);
      const hadDiscount = cartHasFbtDiscount(before, resolveFbtDiscountCode(before, getStoredDiscount()));

      // Always sync when FBT lines or FBT discount are present
      if (fbtBefore === 0 && !hadDiscount && !getStoredDiscount()?.code) return;

      const cart = await syncBundleDiscount();
      if (!cart) return;

      const fbtAfter = countFbtItems(cart);
      const hasDiscount = cartHasFbtDiscount(cart, resolveFbtDiscountCode(cart, getStoredDiscount()));
      const needsRefresh =
        fbtBefore !== fbtAfter ||
        hadDiscount !== hasDiscount ||
        fbtAfter < MIN_FBT_ITEMS_FOR_DISCOUNT;

      if (needsRefresh) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) await refreshCartDrawer(cart);
      }
    } finally {
      setTimeout(() => {
        window.__ggFbtSyncing = false;
      }, 400);
    }
  }

  function bindCartSync() {
    if (window.__ggFbtCartBound) return;
    window.__ggFbtCartBound = true;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, runCartDiscountSync);
    }

    // Backup: cart drawer remove / qty changes
    document.addEventListener(
      'click',
      (e) => {
        if (e.target.closest('cart-remove-button, .cart-remove-button, .quantity__button')) {
          setTimeout(() => runCartDiscountSync({ source: 'fbt-dom' }), 500);
        }
      },
      true
    );

    // Also run once on load
    syncBundleDiscount().then((cart) => {
      if (cart && (hasIncompleteBundle(cart) || countFbtItems(cart) === 0)) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer?.classList?.contains('active')) refreshCartDrawer(cart);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCartSync);
  } else {
    bindCartSync();
  }
})();
